import { inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Category, CreateCategoryRequest, MarketWatchService } from '@easy-lib/modules/market-watch';
import { NewPortfolioSort, NewPortfolioSortKey, NewPortfolioSortType } from '@easy-lib/modules/portfolio';
import { PortfolioItemsWithCalculation } from '@easy-lib/modules/portfolio/models/portfolio-item.model';
import { SettingService } from '@easy-lib/modules/settings';
import { DPortfolioSymbolData, PortfolioSymbolData } from '@easy-lib/modules/shared/models';
import {
	LsService,
	MarketDataService,
	MoneyService,
	NotificationService,
	NotificationType,
	SymbolService,
	TimeService,
	UserDeviceService
} from '@easy-lib/modules/shared/services';
import { Swipeable } from '@easy-lib/modules/swipe';
import { PortfolioFacade } from '@easy-lib/state/portfolio/portfolio.facade';
import { PortfolioStore } from '@easy-lib/state/portfolio/portfolio.store';
import { SortStates } from '@easy-lib/tools/sort';
import { BehaviorSubject, combineLatest, combineLatestWith, Observable, Subject, withLatestFrom } from 'rxjs';
import { filter, map, mergeWith, tap } from 'rxjs/operators';
import { of } from 'rxjs/internal/observable/of';

const PORTFOLIO_LOCAL_STORAGE_SHOW_NUMBERS_KEY = 'portfolio_store_show_numbers';
@Injectable({
	providedIn: 'root'
})
export class PortfolioStateService {
	//#region injections
	portFolioStore: PortfolioStore = inject(PortfolioStore);
	portfolioFacade: PortfolioFacade = inject(PortfolioFacade);
	marketDataService: MarketDataService = inject(MarketDataService);
	timeService: TimeService = inject(TimeService);
	symbolService: SymbolService = inject(SymbolService);
	settingService: SettingService = inject(SettingService);
	lsService: LsService = inject(LsService);
	moneyService: MoneyService = inject(MoneyService);
	marketWatchService: MarketWatchService = inject(MarketWatchService);
	userDeviceService = inject(UserDeviceService);
	notificationService = inject(NotificationService);
	//#endregion injections

	constructor() {
		toSignal(this.needToAddPortfolioTomarketWatchCategory$);
	}
	//#region Portfolio market data

	//#region mobile
	portfolioMarketDataMobile: BehaviorSubject<{ isin: string; marketData: PortfolioSymbolData }[]> =
		new BehaviorSubject([]);
	portfolioMarketDataMobile$ = this.portfolioMarketDataMobile.asObservable();

	portfolioMarketDataMobileCalcedData$ = this.portFolioStore.portfolioItems$;
	//#endregion mobile

	//#region desktop
	portfolioMarketDataDesktop: BehaviorSubject<{ isin: string; marketData: DPortfolioSymbolData }[]> =
		new BehaviorSubject([]);
	portfolioMarketDataDesktop$ = this.portfolioMarketDataDesktop.asObservable();
	portfolioMarketDataDesktopCalcedData$ = this.portFolioStore.getPortfolioAllItems$;
	//#endregion desktop

	//#region calculate total
	portfolioMarketDataMobileCalcedTotalDataInitValue$ = of(null).pipe(
		map(() => ({
			totalAssetsValue: 0,
			totalAssetCount: 0,
			totalPortfolioValue: 0,
			totalBonusValue: 0,
			totalProfitAmount: 0,
			totalProfitPercent: 0,
			totalLastDPSAmount: 0,
			totalTodayProfitAmount: 0,
			totalTodayProfitPercent: 0,
			totalSoldProfit: 0
		}))
	);
	portfolioMarketDataMobileCalcedTotalData$ = this.portfolioMarketDataMobileCalcedTotalDataInitValue$.pipe(
		mergeWith(this.portFolioStore.portfolioItemTotalValues$)
	);
	//#endregion calculate total

	//#endregion Portfolio market data

	//#region swipe item
	swipeItemState$ = new BehaviorSubject<{ symbolIsin: string; swipeState: Swipeable }>(null);
	swipeGritItem(item: PortfolioItemsWithCalculation, swipeState: Swipeable) {
		this.swipeItemState$.next({ symbolIsin: item.symbolIsin, swipeState: swipeState });
	}

	resetAllSwipeGritItem() {
		this.swipeItemState$.next(null);
	}
	//#endregion swipe item

	//#region moreThan 15 items in portfolio
	isPortfolioItemsMoreThanFifteen$ = new BehaviorSubject<boolean>(false);
	//#endregion moreThan 15 items in portfolio

	//#region search state
	toggleSearch$ = new BehaviorSubject<boolean>(false);
	searchPortfolioInputChangeObservable$ = new BehaviorSubject('');
	//#endregion search state

	//#region scrollState
	scrollState = signal<number>(0);
	//#endregion scrollState

	//#region showNumbers
	showNumbers = new BehaviorSubject<boolean>(
		localStorage.getItem(PORTFOLIO_LOCAL_STORAGE_SHOW_NUMBERS_KEY) !== null
			? JSON.parse(localStorage.getItem(PORTFOLIO_LOCAL_STORAGE_SHOW_NUMBERS_KEY))
			: true
	);
	showNumbers$ = this.showNumbers.asObservable();
	changeShowNumbersInDetailWidget() {
		localStorage.setItem(PORTFOLIO_LOCAL_STORAGE_SHOW_NUMBERS_KEY, String(!this.showNumbers.value));
		this.showNumbers.next(!this.showNumbers.value);
	}
	//#endregion showNumbers

	//#region showPortfolio symbol Detail
	showPortfolioSymbolDetail = new BehaviorSubject<string>(null);
	showPortfolioSymbolDetail$ = this.showPortfolioSymbolDetail.asObservable();

	goToItemDetailPage(isin) {
		this.showPortfolioSymbolDetail.next(isin);
	}
	clearItemDetailPage() {
		this.showPortfolioSymbolDetail.next(null);
	}
	//#endregion showPortfolio symbol Detail
	//#region showPortfolioDetail
	showPortfolioDetail = new BehaviorSubject<boolean>(false);
	showPortfolioDetail$ = this.showPortfolioDetail.asObservable();
	changeShowPortfolioDetail(visibilityState) {
		this.showPortfolioDetail.next(visibilityState);
	}
	//#endregion showPortfolioDetail

	//#region sortObservable

	portSort: BehaviorSubject<NewPortfolioSort> = new BehaviorSubject<NewPortfolioSort>(null);

	portSortInitValue$ = of(null).pipe(
		map(() => {
			let portSort = JSON.parse(localStorage.getItem('portfolio_new_sort')) as NewPortfolioSort;

			if (!portSort) {
				portSort = {
					type: NewPortfolioSortType.CurrentValue,
					dir: SortStates.asc,
					key: NewPortfolioSortKey.ValueCurrentProfit
				};
			}
			this.portSort.next({ ...portSort });
			return portSort;
		})
	);
	portSort$ = this.portSortInitValue$.pipe(
		mergeWith(this.portSort.asObservable().pipe(filter(Boolean))),
		tap(val => {
			localStorage.setItem('portfolio_new_sort', JSON.stringify(val));
		})
	);

	portSortKey = new Subject<string>();
	calcSort(sortKey) {
		if (sortKey === NewPortfolioSortKey.SymbolNameQuantity)
			this.changeInTwinSortOption(
				NewPortfolioSortKey.SymbolNameQuantity,
				this.portSort.value,
				NewPortfolioSortType.SymbolName,
				NewPortfolioSortType.Asset
			);
		else if (sortKey === NewPortfolioSortKey.ValueCurrentProfit)
			this.changeInTwinSortOption(
				NewPortfolioSortKey.ValueCurrentProfit,
				this.portSort.value,
				NewPortfolioSortType.CurrentValue,
				NewPortfolioSortType.ProfitAmount
			);
		else if (sortKey === NewPortfolioSortKey.lastPriceNetPrice)
			this.changeInTwinSortOption(
				NewPortfolioSortKey.lastPriceNetPrice,
				this.portSort.value,
				NewPortfolioSortType.LatestPrice,
				NewPortfolioSortType.closePrice
			);
	}

	changeInTwinSortOption(
		newKey: NewPortfolioSortKey,
		prevValue: NewPortfolioSort,
		option1: NewPortfolioSortType,
		option2: NewPortfolioSortType
	) {
		let newValue = { type: option1, dir: SortStates.asc } as NewPortfolioSort;
		if ([option1, option2].includes(prevValue.type)) {
			if (prevValue.type === option1) {
				newValue = {
					type: prevValue.dir === SortStates.asc ? option1 : option2,
					dir: prevValue.dir === SortStates.asc ? SortStates.decs : SortStates.asc
				} as NewPortfolioSort;
			} else if (prevValue.type === option2) {
				newValue = {
					type: prevValue.dir === SortStates.asc ? option2 : NewPortfolioSortType.None,
					dir: prevValue.dir === SortStates.asc ? SortStates.decs : SortStates.none
				} as NewPortfolioSort;
			}
		}
		newValue.key = newKey;
		this.portSort.next({ ...newValue });
	}
	//#endregion sortObservable

	//#region portfolio - market-watch
	isinsToPortfolioMarketWatch = new Subject<string>();
	isinsToPortfolioMarketWatch$ = this.isinsToPortfolioMarketWatch.asObservable();

	readonly categoryName = 'پرتفوی';
	isPortfolioAddedToMarketWatch$ = this.marketWatchService.getCategories().pipe(
		mergeWith(this.marketWatchService.categoriesObs$),
		map((categories: Category[]) => {
			return categories.some(x => x.name === this.categoryName);
		})
	);
	isPortfolioAddedToMarketWatch = toSignal(this.isPortfolioAddedToMarketWatch$);

	needToAddPortfolioTomarketWatchCategory$ = this.isPortfolioAddedToMarketWatch$.pipe(
		combineLatestWith(this.isinsToPortfolioMarketWatch$, this.marketWatchService.categoriesObs$),
		withLatestFrom(this.portFolioStore.portfolioItems$.pipe(map(items => [...items.map(x => x.symbolIsin)]))),
		tap(([[isPortfolioAddedToMarketWatch, categoryId, categories], portfolioItemIsins]) => {
			if (categoryId && portfolioItemIsins?.length > 0) {
				const portfolioCatrgory = categories.find(x => x.id === categoryId);
				const categorySymbolIsins = portfolioCatrgory.categorySymbols.map(symbol => symbol.symbolIsin);
				const needToAddIsins = portfolioItemIsins.filter(isin => !categorySymbolIsins.includes(isin));
				const needToRemoveIsins = categorySymbolIsins.filter(isin => !portfolioItemIsins.includes(isin));
				const addOrRemoveSubs: Observable<any>[] = [];
				if (needToAddIsins?.length)
					addOrRemoveSubs.push(
						this.marketWatchService.addBatchSymbolToCategory(portfolioCatrgory, needToAddIsins)
					);

				if (needToRemoveIsins?.length)
					addOrRemoveSubs.push(
						this.marketWatchService.deleteSymbolsFromCategory(portfolioCatrgory, needToRemoveIsins)
					);
				if (addOrRemoveSubs.length > 0) {
					combineLatest(addOrRemoveSubs).subscribe(() => {
						this.notificationService.alert(
							isPortfolioAddedToMarketWatch
								? 'دیده بان پرتفوی ایجاد گردید'
								: 'دیده بان پرتفوی بروز گردید',
							NotificationType.success
						);
					});
				} else {
					this.notificationService.alert('دیده بان پرتفوی بروز گردید', NotificationType.success);
				}

				this.isinsToPortfolioMarketWatch.next(null);
			}
		})
	);
	addPortfolioToMarketWatch() {
		if (!this.isPortfolioAddedToMarketWatch()) {
			const category: CreateCategoryRequest = {
				name: this.categoryName,
				isDefault: false,
				order: this.marketWatchService.categories.length - 1
			};
			this.marketWatchService
				.createCategory(category)
				.pipe(
					tap(newCategory => {
						this.isinsToPortfolioMarketWatch.next(newCategory.id);
					})
				)
				.subscribe();
		} else {
			this.isinsToPortfolioMarketWatch.next(
				this.marketWatchService.categories.find(x => x.name === this.categoryName).id
			);
		}
	}
	//#endregion portfolio - market-watch
}
